import { createContext, useContext, ReactNode } from 'react';
import { useActiveBusinessProfile } from '../hooks/business/useActiveBusinessProfile';
import type { Profile } from '../types/profile';

interface ActiveBusinessContextType {
  activeProfile: Profile | null;
  profileLoading: boolean;
  profileError: Error | null;
  updateActiveProfile: ((updates: Partial<Profile>) => Promise<Profile | null>) | undefined;
  reloadActiveProfile: () => Promise<void>;
  unclaimBusiness: (businessId: number) => Promise<boolean>;
}

const ActiveBusinessContext = createContext<ActiveBusinessContextType | undefined>(undefined);

export function ActiveBusinessProvider({ children }: { children: ReactNode }) {
  const {
    profile: activeProfile,
    loading: profileLoading,
    error: profileError,
    updateProfile: updateActiveProfile,
    unclaimBusiness,
    reloadProfile: reloadActiveProfile
  } = useActiveBusinessProfile();

  const value: ActiveBusinessContextType = {
    activeProfile,
    profileLoading,
    profileError,
    updateActiveProfile,
    reloadActiveProfile,
    unclaimBusiness
  };

  return (
    <ActiveBusinessContext.Provider value={value}>
      {children}
    </ActiveBusinessContext.Provider>
  );
}

export function useActiveBusiness() {
  const context = useContext(ActiveBusinessContext);
  if (context === undefined) {
    throw new Error('useActiveBusiness must be used within an ActiveBusinessProvider');
  }
  return context;
}

// Convenience hook for components that only need the profile
export function useActiveProfile() {
  const { activeProfile, profileLoading, profileError, updateActiveProfile } = useActiveBusiness();
  return { profile: activeProfile, loading: profileLoading, error: profileError, updateProfile: updateActiveProfile };
}
